import { useCallback } from 'react';

import { TaxonomyType } from '@revelio/data-access';

import {
  BranchLabelMenu,
  BranchLabelMenuItem,
} from '../../../../../shared/components/tree/PopoutTree/BranchLabelMenu';
import { useGeographyTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useGeographyTaxonomyTree';
import { useTaxonomyList } from '../../../../../shared/selection-lists/hooks/useTaxonomyList';
import {
  getGeographyLevelChain,
  getGeographyLevelLabel,
  getGeographyTaxonomyLabel,
} from '../../../../../shared/selection-lists/utils/geographyTaxonomyLabels';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../../shared/user-preferences';
import { PreferenceBrowserStorage } from '../../../../../shared/user-preferences/utils/preference-session-storage';
import { updateCustomDashFiltersForPreference } from '../../../../../shared/user-preferences/utils/preferences';
import { useFilterContext } from '../../contexts/FilterProvider';
import { useGeographyTaxonomy } from '../../hooks/useGeographyTaxonomy';
import { getGeographyDefaultSelectionsForTaxonomy } from '../../utils/getGeographyDefaultSelectionsForTaxonomy';

/**
 * This label replaces the branch heading on the THIRD tree panel, so both the
 * heading and the menu describe the taxonomy from that depth down — the two
 * shallow levels are already behind the user.
 */
const GEOGRAPHY_BRANCH_LABEL_DEPTH = 2;

/**
 * One switch option, labelled with the levels it would give you from this depth
 * down — "State › County". Its own component so it can fetch its taxonomy's
 * levels; the app-start warm-up means that resolves from cache.
 */
const GeographyTaxonomyMenuItem = ({
  taxonomyId,
  name,
  version,
  onSelect,
}: {
  taxonomyId: number;
  name: string;
  version?: string;
  onSelect: (taxonomyId: number, version?: string) => void;
}) => {
  const { levels } = useGeographyTaxonomyTree({
    taxonomyId,
  });

  return (
    <BranchLabelMenuItem
      value={String(taxonomyId)}
      onSelect={() => onSelect(taxonomyId, version)}
    >
      {getGeographyLevelChain(levels, GEOGRAPHY_BRANCH_LABEL_DEPTH) ||
        getGeographyTaxonomyLabel(name, levels)}
    </BranchLabelMenuItem>
  );
};

/**
 * Renders an inline dropdown that replaces the branch label in the 3rd popout
 * panel of the geography tree, allowing users to switch geography taxonomy
 * directly from the tree. Options come from `taxonomyList`, so this stays in
 * step with the preferences-modal picker.
 *
 * The heading names only the level being shown ("State"); the menu spells out
 * where each choice leads ("State › County"), so the switch is legible without
 * the collapsed label carrying a chain the user hasn't reached yet.
 */
export const GeographyTaxonomyBranchLabel = () => {
  const { taxonomies } = useTaxonomyList({
    taxonomyType: TaxonomyType.Geography,
  });
  const { taxonomyId, levels } = useGeographyTaxonomy();
  const { dispatch } = useFilterContext();

  const currentLevelLabel =
    getGeographyLevelLabel(levels, GEOGRAPHY_BRANCH_LABEL_DEPTH) ||
    'Metro Area';

  const onSelect = useCallback(
    (nextId: number, version?: string) => {
      const value = { taxonomyId: nextId, ...(version ? { version } : {}) };
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.Geography,
        value
      );
      // Same regeneration as the preferences modal — switching from the tree must
      // not leave the previous taxonomy's defaults behind.
      PreferenceBrowserStorage.update(
        PreferenceScope.GEOGRAPHY,
        UserPreferenceKey.DefaultFilters,
        getGeographyDefaultSelectionsForTaxonomy(nextId)
      );
      // `useGeographyTaxonomy` resolves from the GEOGRAPHY_TAXONOMY filter
      // first, so a preference write alone leaves the previous value shadowing
      // this choice. The preferences modal gets this dispatch through
      // `onPreferenceUpdate`; this picker has to make it itself.
      updateCustomDashFiltersForPreference(
        dispatch,
        UserPreferenceKey.Geography,
        value
      );
    },
    [dispatch]
  );

  return (
    <BranchLabelMenu label={currentLevelLabel} value={String(taxonomyId)}>
      {taxonomies.map((taxonomy) => (
        <GeographyTaxonomyMenuItem
          key={taxonomy.id}
          taxonomyId={taxonomy.id}
          name={taxonomy.name}
          version={taxonomy.version}
          onSelect={onSelect}
        />
      ))}
    </BranchLabelMenu>
  );
};
